import { memo, useCallback } from "react";
import { IconButton } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { useTranslation } from "i18n-fe";
import ClassGridRow from "./ClassGridRow";
import { rowDropId } from "../util/sectionGestures";

// #3293 - one scoring row of the section grid: the ordinary ClassGridRow plus
// a drag handle (the rider number cell) and a drop target for reordering.
//
// The drag handle is the ONLY drag activator, not the whole row: the row is
// full of number inputs, and a long-press to drag must not fight a long-press
// to select text. Touch: dnd-kit's TouchSensor (ClassGrid) needs a press-and-
// hold before it lifts, so a scroll is never a drag. Keyboard: the handle is
// focusable; ClassGrid's key handler owns Space / arrows (see its comment).
function SectionRow({
  ride,
  locked,
  hidden,
  dropLine,
  lifted,
  onHandleKeyDown,
  ...rowProps
}) {
  const { t } = useTranslation("score");
  const drop = useDroppable({ id: rowDropId(ride.id), disabled: locked });
  const drag = useDraggable({ id: ride.id, disabled: locked });
  const { setNodeRef: setDropNode } = drop;
  const { setNodeRef: setDragNode } = drag;
  // The row node is BOTH the reorder drop target and the draggable's node.
  // dnd-kit measures the dragged node for its DragOverlay and for collision
  // detection; with no node set, a real pointer drag never reports a target
  // and shows no overlay (found in real Chrome, #3293 AC19; the RTL drag tests
  // call the drop handlers directly).
  const setRowNode = useCallback(
    (el) => {
      setDropNode(el);
      setDragNode(el);
    },
    [setDropNode, setDragNode],
  );

  const handle = (
    <IconButton
      ref={drag.setActivatorNodeRef}
      {...drag.attributes}
      {...(locked ? {} : drag.listeners)}
      onKeyDown={locked ? undefined : (e) => onHandleKeyDown?.(e, ride.id)}
      disabled={locked}
      aria-label={t("classGrid.sections.dragHandleLabel", { number: ride.competitorNumber ?? "" })}
      aria-pressed={lifted ? "true" : "false"}
      data-testid={`section-handle-${ride.competitorNumber ?? ride.id}`}
      size="small"
      sx={{ touchAction: "none", cursor: locked ? "default" : "grab", minWidth: 40, minHeight: 40 }}
    >
      <DragIndicatorIcon fontSize="small" />
    </IconButton>
  );

  return (
    <ClassGridRow
      {...rowProps}
      ride={ride}
      sectionRow={{
        setDropRef: setRowNode,
        handle,
        dropLine,
        dragging: drag.isDragging || lifted,
        hidden,
      }}
    />
  );
}

export default memo(SectionRow);
